// React and Hooks
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

// Internal Components & Services
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import BlogPostComponent from "./components/BlogPostComponent"; // Renders Sanity block content
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";
import { useLoadingContent } from "../../hooks/useLoadingContent";
import { usePersistentLoading } from "../../hooks/usePersistentLoading";
import { client } from "../../services/client";

// Styling
import "./BlogPostScreen.css";
import SEO from "../../components/seo/SEO";

function BlogPostScreen() {
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { slug } = useParams();
  const { userInfo } = useSelector((state) => state.userLogin);
  const loadingContent = useLoadingContent();
  const { isShowing, tileProps } = usePersistentLoading(
    isLoading,
    loadingContent
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // This single, efficient query gets the current post, its author, and the next/previous posts.
        const query = `
          *[_type == "post" && slug.current == $slug][0]{
            title,
            "authorName": author->name,
            mainImage{asset->{url}},
            publishedAt,
            subtitle,
            caption,
            body,
            "previousPost": *[_type == "post" && publishedAt < ^.publishedAt] | order(publishedAt desc)[0]{title, "slug": slug.current},
            "nextPost": *[_type == "post" && publishedAt > ^.publishedAt] | order(publishedAt asc)[0]{title, "slug": slug.current}
          }`;

        const params = { slug };
        const response = await client.fetch(query, params);
        setPost(response);
      } catch (error) {
        console.error("Error fetching post data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchData();
    }
  }, [slug]); // Re-run this effect only when the slug changes

  // Helper function to format the date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // SEO and Helmet metadata
  const helmetData = {
    title: post?.title || "Blog Post",
    description:
      post?.body?.[0]?.children?.[0]?.text?.substring(0, 155) ||
      "An article from No Wasted Days.",
    image: post?.mainImage?.asset?.url,
  };

  if (isShowing) {
    return <LoadingTile content={loadingContent} {...tileProps} />;
  }

  if (!post) {
    return (
      <>
        <SEO
          title={helmetData.title}
          description={helmetData.description}
          canonicalUrl={`https://www.nowasteddays.io/${slug}`}
        />
        <Header variant="light" active={true} />
        <main className="blog-post__main">
          <div className="blog-post__not-found">
            <h1>Post not found</h1>
            <p>Sorry, we couldn't find the blog post you were looking for.</p>
            <Link to="/blog" className="blog-post__nav-link">
              ← Back to Blog
            </Link>
          </div>
        </main>
        <TestFooter />
      </>
    );
  }

  const ImageBlock = () => {
    if (!post.mainImage) return;

    return (
      <div className="image__wrapper">
        <img
          className="blog-post__main-image"
          src={post.mainImage.asset.url}
          alt={post.title}
        />
        {post.caption && <caption>{post.caption}</caption>}
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{helmetData.title}</title>
        <meta property="og:title" content={helmetData.title} />
        <meta property="og:description" content={helmetData.description} />
        {helmetData.image && (
          <meta property="og:image" content={helmetData.image} />
        )}
        <meta
          property="og:url"
          content={`https://nowasteddays.io/blog/${slug}`}
        />
        <meta property="og:type" content="article" />
      </Helmet>

      <Header variant="light" />
      <main className="blog-post__main">
        <article className="blog-post__content">
          <header className="blog-post__header">
            <h1 className="blog-post__title">{post.title}</h1>
            {post.subtitle && (
              <p className="blog-post__meta" style={{ opacity: "0.5" }}>
                {post.subtitle}
              </p>
            )}
            {/* <p className="blog-post__meta">
              {post.authorName && `${post.authorName}`}
            </p> */}
            <hr></hr>
          </header>

          {post.mainImage && <ImageBlock />}

          <div className="blog-post__body">
            <BlogPostComponent body={post.body} />
          </div>
        </article>

        <nav className="blog-post__navigation">
          <div className="blog-post__nav-item">
            {post.previousPost ? (
              <Link
                to={`/blog/${post.previousPost.slug}`}
                className="blog-post__nav-link"
              >
                <span>← Previous</span>
                <strong>{post.previousPost.title}</strong>
              </Link>
            ) : (
              <div />
            )}
          </div>
          <div className="blog-post__nav-item blog-post__nav-item--next">
            {post.nextPost ? (
              <Link
                to={`/blog/${post.nextPost.slug}`}
                className="blog-post__nav-link"
              >
                <span>Next →</span>
                <strong>{post.nextPost.title}</strong>
              </Link>
            ) : (
              <div />
            )}
          </div>
        </nav>
      </main>
      <TestFooter />
    </>
  );
}

export default BlogPostScreen;
